import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import { useT } from "../lib/i18n";
import { SAVI_IMAGES, SAVI_GALLERY, SAVI_SOCIAL } from "../config/saviImages";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import Confetti from "../components/Confetti";
import { InstallAppCard } from "../components/InstallAppCard";
import { InstallInstructions } from "../components/InstallInstructions";
import { PushPromptCard } from "../components/PushPromptCard";
import AvatarNudgeBanner from "../components/AvatarNudgeBanner";
import {
  Sun,
  Cloud,
  CloudRain,
  Wind,
  Droplets,
  MapPin,
  Calendar,
  Clock,
  Bell,
  Users,
  Home,
  User,
  LogOut,
  ChevronRight,
  Utensils,
  Mountain,
  Wifi,
  Thermometer,
  Check,
  CheckCircle,
  X,
  HelpCircle,
  ChevronLeft,
  Coffee,
  Map,
  FileText,
  Download,
  BookOpen,
  KeyRound,
  Lightbulb,
  Star,
  Trophy,
  Car,
  Sparkles,
  ClipboardList,
  Eye,
  EyeOff,
} from "lucide-react";
import PassportLeaderboard from "../components/PassportLeaderboard";
import ShoppingRun from "../components/ShoppingRun";
import HomeTab from "../components/home/HomeTab";
import PortalBottomNav from "../components/PortalBottomNav";
import compressImage from "../lib/compressImage";
import ImageCropDialog from "../components/ImageCropDialog";

// Format date to DD/MM/YYYY
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Format date to "Tue 3 Mar" style
const formatDateShort = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
};

// Shared "New event" posts are written once in English; retreat guests read them in German, without the RSVP nudge.
const DE_DAYS = { Mon: "Mo", Tue: "Di", Wed: "Mi", Thu: "Do", Fri: "Fr", Sat: "Sa", Sun: "So" };
const DE_MONTHS = { Jan: "Jan", Feb: "Feb", Mar: "März", Apr: "Apr", May: "Mai", Jun: "Juni", Jul: "Juli", Aug: "Aug", Sep: "Sept", Oct: "Okt", Nov: "Nov", Dec: "Dez" };
const localizeRetreatUpdate = (msg = "") =>
  msg
    .replace(/\s*Are you in\?\s*$/, "")
    .replace(/^📅 New event:/, "📅 Neu im Programm:")
    .replace(/ — (Mon|Tue|Wed|Thu|Fri|Sat|Sun) (\d{1,2}) ([A-Z][a-z]{2}) at (\d{1,2}:\d{2})\./, (_, d, n, m, time) => ` — ${DE_DAYS[d]}, ${n}. ${DE_MONTHS[m] || m} um ${time} Uhr.`)
    .replace(/ — (Mon|Tue|Wed|Thu|Fri|Sat|Sun) (\d{1,2}) ([A-Z][a-z]{2})\./, (_, d, n, m) => ` — ${DE_DAYS[d]}, ${n}. ${DE_MONTHS[m] || m}.`);


const GuestDashboard = () => {
  const { user, token, logout, refreshUser } = useAuth();
  const { t, locale } = useT();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [weather, setWeather] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "home");
  const [todayActivities, setTodayActivities] = useState(null);
  const [weekEvents, setWeekEvents] = useState([]);
  const [todayEvents, setTodayEvents] = useState([]);
  const [weekEspressoTalks, setWeekEspressoTalks] = useState([]);
  const [topMatches, setTopMatches] = useState([]);
  const [todayVolunteerTasks, setTodayVolunteerTasks] = useState([]);
  const [showFirstWeekSheet, setShowFirstWeekSheet] = useState(false);
  // Hero image: start from the cached URL so the configured photo shows first (no fallback flash)
  const [heroConfig, setHeroConfig] = useState(() => {
    const cached = localStorage.getItem("savi_hero_url");
    return { url: cached || "", loaded: !!cached };
  });
  // Edit Profile modal
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editingProfile, setEditingProfile] = useState({
    name: "",
    nationality: "",
    occupation: "",
    current_project: "",
    fun_fact: "",
    avatar: "",
    bringing_pet: false,
    pet_type: "dog",
    pet_name: "",
    pet_breed: "",
    pet_photo: ""
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Espresso Talk quick form
  const [showQuickTalk, setShowQuickTalk] = useState(false);
  const [quickTalk, setQuickTalk] = useState({
    topic: "",
    date: "",
    time: "10:00",
    location: "Living Room"
  });
  const [submittingTalk, setSubmittingTalk] = useState(false);
  const [overlapWarning, setOverlapWarning] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [steveMessage, setSteveMessage] = useState("");
  const [youAsked, setYouAsked] = useState([]);
  const [todayRides, setTodayRides] = useState([]);
  const [weekRides, setWeekRides] = useState([]);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: "", next: "", confirm: "" });
  const [showPwFields, setShowPwFields] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const [eventRsvpMap, setEventRsvpMap] = useState({});

  useEffect(() => {
    const keys = notifications.filter((n) => n.event_id).map((n) => n.event_id);
    if (keys.length === 0) return;
    axios
      .get(`${API}/community/events/my-rsvps?keys=${keys.join(",")}`, { headers })
      .then((res) => {
        const m = {};
        (res.data?.going || []).forEach((k) => { m[k] = "in"; });
        setEventRsvpMap((prev) => ({ ...m, ...prev }));
      })
      .catch(() => {});
  }, [notifications]); // eslint-disable-line react-hooks/exhaustive-deps

  const rsvpFromUpdate = async (eventId, going) => {
    try {
      await axios.post(`${API}/community/events/rsvp`, { occurrence_key: eventId, going }, { headers });
      setEventRsvpMap((prev) => ({ ...prev, [eventId]: going ? "in" : "out" }));
      toast.success(going ? "You're in! 🙌" : "No worries — marked as not going");
    } catch {
      toast.error("Couldn't save your RSVP");
    }
  };

  const handleEventApproval = async (eventId, approve) => {
    try {
      await axios.post(`${API}/admin/events/${eventId}/${approve ? "approve" : "deny"}`, {}, { headers });
      toast.success(approve ? "Event approved — the house has been invited 🎉" : "Event declined — the creator has been notified");
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update the event");
    }
  };

  // Check for first-time login welcome animation
  useEffect(() => {
    const isFirstLogin = localStorage.getItem("savi_first_login");
    if (isFirstLogin) {
      setShowWelcome(true);
      localStorage.removeItem("savi_first_login");
      // Hide welcome after animation
      setTimeout(() => setShowWelcome(false), 4000);
    }
  }, []);

  // Redirect to the onboarding wizard if the admin force-reset their progress
  // (or they reach the dashboard before finishing). Mirrors the login redirect logic.
  useEffect(() => {
    if (user && user.role === "guest" && !user.onboarding?.final_confirmed) {
      navigate(user.guest_type === "retreat" ? "/retreat-welcome" : "/onboarding", { replace: true });
    }
  }, [user, navigate]);

  // Update active tab when URL params change
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab && ["home", "notifications", "profile"].includes(tab)) {
      setActiveTab(tab);
    } else if (!tab) {
      setActiveTab("home");
    }
  }, [searchParams]);

  // Scroll to top when tab changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeTab]);

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [weatherRes, announcementsRes, notificationsRes, todayRes, talksRes, steveMessageRes, ridesRes, youAskedRes, heroRes, matchesRes, weekRes, passportRes] = await Promise.all([
        axios.get(`${API}/weather`),
        axios.get(`${API}/announcements`, { headers }),
        axios.get(`${API}/notifications`, { headers }),
        axios.get(`${API}/community/today`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/community/espresso-talks`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/site-settings/steve_message`).catch(() => ({ data: { value: "" } })),
        axios.get(`${API}/rides`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/community/you-asked`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/site-settings/dashboard_hero`).catch(() => ({ data: { value: "" } })),
        axios.get(`${API}/ai/matches`, { headers }).catch(() => ({ data: { matches: [] } })),
        axios.get(`${API}/community/weekly-schedule?weeks=2&weeks_back=0`, { headers }).catch(() => ({ data: { calendar: [] } })),
        axios.get(`${API}/passport`, { headers }).catch(() => ({ data: null })),
      ]);
      
      setWeather(weatherRes.data);
      setAnnouncements(announcementsRes.data);
      setNotifications(notificationsRes.data);
      setTodayActivities(todayRes.data);
      setWeekEspressoTalks(talksRes.data || []);
      setSteveMessage(steveMessageRes.data?.value || "");
      setYouAsked(youAskedRes.data || []);
      setTopMatches((matchesRes.data?.matches || []).slice(0, 3));

      // Today + week ahead from the same calendar (ids, RSVP, tags), already sorted by (date, time) server-side
      const fmtDate = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
      const wkStart = new Date(); wkStart.setDate(wkStart.getDate() + 1);
      const wkEnd = new Date(); wkEnd.setDate(wkEnd.getDate() + 7);
      const calendar = weekRes.data?.calendar || [];
      const todayKey = fmtDate(new Date());
      setTodayEvents(calendar.filter(e => e.date === todayKey));
      setWeekEvents(calendar.filter(e => e.date >= fmtDate(wkStart) && e.date <= fmtDate(wkEnd)));

      // Rides: today + the week ahead (skip cancelled)
      const liveRides = (ridesRes.data || []).filter(r => r.status !== "cancelled");
      const todayStr = fmtDate(new Date());
      setTodayRides(liveRides.filter(r => r.date === todayStr));
      setWeekRides(liveRides.filter(r => r.date >= fmtDate(wkStart) && r.date <= fmtDate(wkEnd)));

      // Celebrate freshly-awarded House Points badges (once per badge, per device)
      if (passportRes.data) {
        const earned = passportRes.data.badges || [];
        const seen = JSON.parse(localStorage.getItem("savi_seen_badges") || "null");
        if (seen !== null) {
          const fresh = earned.filter((b) => !seen.includes(b.id));
          if (fresh.length > 0) {
            setShowConfetti(true);
            setTimeout(() => setShowConfetti(false), 150);
            toast.success(`New badge earned: ${fresh[0].icon || "🏅"} ${fresh[0].name}! +${fresh[0].points || 0} pts`, { duration: 6000 });
          }
        }
        localStorage.setItem("savi_seen_badges", JSON.stringify(earned.map((b) => b.id)));
      }

      // Volunteers see "my shifts today"; admins get a leader view of the
      // full team roster for today. Both pull from the same weekly plan.
      const isTeamViewer = user?.is_volunteer || user?.role === "admin";
      if (isTeamViewer) {
        try {
          const today = new Date();
          const dow = today.getDay();
          const diff = dow === 0 ? -6 : 1 - dow;
          const mon = new Date(today); mon.setDate(today.getDate() + diff); mon.setHours(0,0,0,0);
          const iso = `${mon.getFullYear()}-${String(mon.getMonth()+1).padStart(2,"0")}-${String(mon.getDate()).padStart(2,"0")}`;
          const dayKeys = ["sun","mon","tue","wed","thu","fri","sat"]; // Date.getDay() index
          const todayKey = dayKeys[dow];
          const planRes = await axios.get(`${API}/guest/volunteer-schedule/${iso}`, { headers });
          const tasks = planRes.data?.tasks || [];
          if (user?.role === "admin") {
            // Leader view — every task that has at least one assignee today.
            const all = tasks
              .map((t) => ({
                name: t.name,
                highlight: t.highlight,
                names: t.assignments?.[todayKey] || [],
                allNames: t.assignments?.[todayKey] || [],
              }))
              .filter((t) => t.names.length > 0);
            setTodayVolunteerTasks(all);
          } else {
            // Accent/case-insensitive token matching so "Maren" matches "Maren Müller",
            // "Maren Senta" (combined assignment), "maren m." etc.
            const norm = (s) => (s || "")
              .toString()
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "") // strip diacritics
              .toLowerCase()
              .trim();
            const myTokens = new Set(
              [
                user?.profile?.preferred_name,
                user?.profile?.first_name,
                user?.name,
              ]
                .filter(Boolean)
                .flatMap((s) => norm(s).split(/[\s,/]+/))
                .filter((t) => t.length >= 2),
            );
            const isMineName = (n) => {
              const tokens = norm(n).split(/[\s,/]+/).filter(Boolean);
              return tokens.some((t) => myTokens.has(t));
            };
            const mine = tasks
              .map((t) => ({
                name: t.name,
                highlight: t.highlight,
                names: (t.assignments?.[todayKey] || []).filter(isMineName),
                allNames: t.assignments?.[todayKey] || [],
              }))
              .filter((t) => t.names.length > 0);
            setTodayVolunteerTasks(mine);
          }
        } catch (e) {
          // Silently ignore — section just stays hidden if the plan isn't seeded yet.
          setTodayVolunteerTasks([]);
        }
      }
      // Parse hero JSON (or fall back to a plain URL string)
      const heroRaw = heroRes.data?.value;
      let heroUrlValue = "";
      if (heroRaw) {
        try {
          heroUrlValue = JSON.parse(heroRaw).url || "";
        } catch {
          heroUrlValue = heroRaw;
        }
      }
      setHeroConfig({ url: heroUrlValue, loaded: true });
      if (heroUrlValue) localStorage.setItem("savi_hero_url", heroUrlValue);
      else localStorage.removeItem("savi_hero_url");
      
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const markNotificationRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`, {}, { headers });
      setNotifications(notifications.map(n => n.id === id ? { ...n, read: true } : n));
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const markAnnouncementRead = async (id) => {
    try {
      await axios.put(`${API}/announcements/${id}/read`, {}, { headers });
      setAnnouncements(announcements.map(a => a.id === id ? { ...a, read: true } : a));
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const changePassword = async () => {
    if (!passwordForm.current || !passwordForm.next || !passwordForm.confirm) {
      toast.error("Please fill in all fields");
      return;
    }
    if (passwordForm.next.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (passwordForm.next !== passwordForm.confirm) {
      toast.error("New passwords don't match");
      return;
    }
    setChangingPassword(true);
    try {
      await axios.put(
        `${API}/guest/password`,
        { current_password: passwordForm.current, new_password: passwordForm.next },
        { headers }
      );
      toast.success("Password updated!");
      setShowChangePassword(false);
      setPasswordForm({ current: "", next: "", confirm: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Couldn't update password");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("See you soon!");
  };

  // Calculate countdown to arrival
  const getCountdown = () => {
    if (!user?.arrival?.arrival_date) return null;
    const arrival = new Date(user.arrival.arrival_date);
    const now = new Date();
    const diff = Math.ceil((arrival - now) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  const countdown = getCountdown();

  // Check if user has been at Savi for more than a week
  const isFirstWeekOver = () => {
    if (!user?.arrival?.arrival_date) return false;
    const arrival = new Date(user.arrival.arrival_date);
    const now = new Date();
    const daysSinceArrival = Math.floor((now - arrival) / (1000 * 60 * 60 * 24));
    return daysSinceArrival > 7;
  };

  const showFirstWeekTips = !isFirstWeekOver();

  // Open edit profile modal
  const openEditProfile = () => {
    setEditingProfile({
      name: user?.profile?.name || user?.name || "",
      nationality: user?.profile?.nationality || "",
      occupation: user?.profile?.occupation || "",
      current_project: user?.profile?.current_project || "",
      fun_fact: user?.profile?.fun_fact || "",
      avatar: user?.profile?.avatar || "",
      bringing_pet: user?.profile?.bringing_pet || false,
      pet_type: user?.profile?.pet_type || "dog",
      pet_name: user?.profile?.pet_name || "",
      pet_breed: user?.profile?.pet_breed || "",
      pet_photo: user?.profile?.pet_photo || ""
    });
    setShowEditProfile(true);
  };

  // Handle avatar upload — user positions/crops before storing so faces aren't auto-cut.
  const [avatarCropFile, setAvatarCropFile] = useState(null);
  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) {
      toast.error("Image must be less than 12MB");
      return;
    }
    setAvatarCropFile(file);
    e.target.value = "";
  };
  const uploadAvatar = async (croppedFile) => {
    try {
      const compressed = await compressImage(croppedFile);
      const formData = new FormData();
      formData.append("file", compressed);
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" }
      });
      setEditingProfile((prev) => ({ ...prev, avatar: response.data.url }));
      toast.success("Photo uploaded!");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload photo");
    } finally {
      setAvatarCropFile(null);
    }
  };

  // Handle pet photo upload
  const handlePetPhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 12 * 1024 * 1024) {
        toast.error("Image must be less than 12MB");
        return;
      }
      
      try {
        const compressed = await compressImage(file);
        const formData = new FormData();
        formData.append("file", compressed);
        
        const response = await axios.post(`${API}/upload/image`, formData, {
          headers: {
            ...headers,
            "Content-Type": "multipart/form-data"
          }
        });
        
        setEditingProfile({ ...editingProfile, pet_photo: response.data.url });
        toast.success("Pet photo uploaded!");
      } catch (error) {
        console.error("Upload error:", error);
        toast.error("Failed to upload photo");
      }
    }
  };

  // Save profile
  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.put(`${API}/guest/profile`, editingProfile, { headers });
      toast.success("Profile updated!");
      setShowEditProfile(false);
      // Refresh user data
      if (refreshUser) refreshUser();
      window.location.reload(); // Fallback to reload
    } catch (error) {
      toast.error("Couldn't save profile");
    } finally {
      setSavingProfile(false);
    }
  };


  // Get weather icon — match WeatherAPI condition text (not OpenWeatherMap codes)
  const WeatherIcon = ({ icon, description, className = "w-8 h-8" }) => {
    const d = (description || "").toLowerCase();
    if (/(rain|drizzle|shower|thunder|storm|sleet|snow)/.test(d)) return <CloudRain className={className} />;
    if (/(cloud|overcast|mist|fog|haze)/.test(d)) return <Cloud className={className} />;
    return <Sun className={className} />;
  };

  // 3-day weather outlook strip
  const ForecastStrip = () => {
    if (!weather?.forecast?.length) return null;
    const dayLabel = (dateStr, idx) => {
      if (idx === 0) return "Today";
      return new Date(dateStr + "T12:00:00").toLocaleDateString("en-GB", { weekday: "short" });
    };
    return (
      <div className="bg-white rounded-2xl p-3 border border-stone-100 mt-2" data-testid="weather-forecast-strip">
        <p className="text-[10px] uppercase tracking-wide text-[#1A1A1A]/50 font-medium mb-2 px-1">3-day outlook · Náquera</p>
        <div className="grid grid-cols-3 divide-x divide-stone-100">
          {weather.forecast.slice(0, 3).map((day, idx) => (
            <div key={day.date} className="flex flex-col items-center gap-1 px-1" data-testid={`forecast-day-${idx}`}>
              <p className="text-xs font-medium text-[#1A1A1A]">{dayLabel(day.date, idx)}</p>
              <WeatherIcon description={day.description} className="w-6 h-6 text-[#8C9A7C]" />
              <p className="text-xs text-[#1A1A1A]">
                <span className="font-semibold">{day.max_temp}°</span>
                <span className="text-[#1A1A1A]/40"> / {day.min_temp}°</span>
              </p>
              {day.chance_of_rain >= 30 ? (
                <p className="text-[10px] text-[#7C8A6C] flex items-center gap-0.5">
                  <CloudRain className="w-3 h-3" /> {day.chance_of_rain}%
                </p>
              ) : (
                <p className="text-[10px] text-[#1A1A1A]/40 truncate max-w-full">{day.description}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Onboarding progress — guest steps only (contracts handled outside the app).
  // For volunteers, drop the arrival step from the calculation since it's auto-completed.
  const onboarding = user?.onboarding || {};
  const guestSteps = user?.is_volunteer
    ? [
        onboarding.profile_completed,
        onboarding.rules_accepted,
        onboarding.activities_selected,
      ]
    : [
        onboarding.profile_completed,
        onboarding.rules_accepted,
        onboarding.arrival_submitted,
        onboarding.activities_selected,
      ];
  const guestStepsComplete = guestSteps.filter(Boolean).length;
  const progressPercent = Math.round((guestStepsComplete / guestSteps.length) * 100);


  const renderNotifications = () => {
    const isRetreatGuest = user?.guest_type === "retreat";
    // Combine announcements and notifications into one list, avoiding duplicates
    const combinedUpdates = [];
    
    // Add announcements (now with read status from backend)
    announcements.forEach(ann => {
      combinedUpdates.push({
        id: ann.id,
        type: 'announcement',
        title: ann.title,
        message: ann.content,
        icon: ann.type,
        created_at: ann.created_at,
        read: ann.read || false
      });
    });
    
    // Add notifications (filter out any that might duplicate announcements)
    notifications.forEach(notif => {
      combinedUpdates.push({
        id: notif.id,
        type: notif.shared ? 'community' : 'personal',
        // Retreat guests are automatically in — drop the RSVP nudge and put the event post in German
        message: isRetreatGuest ? localizeRetreatUpdate(notif.message) : notif.message,
        event_id: notif.event_id,
        pending_approval: notif.pending_approval,
        approval_result: notif.approval_result,
        created_at: notif.created_at,
        read: notif.read
      });
    });
    
    // Sort by date, newest first
    combinedUpdates.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    const deleteUpdate = async (item) => {
      try {
        if (item.type === 'announcement') {
          // Dismiss announcement for this user
          await axios.delete(`${API}/announcements/${item.id}/dismiss`, { headers });
        } else {
          // Delete notification
          await axios.delete(`${API}/notifications/${item.id}`, { headers });
        }
        // Refresh data
        fetchData();
        toast.success("Update removed");
      } catch (error) {
        toast.error("Couldn't remove update");
      }
    };

    const clearAllNotifications = async () => {
      try {
        await axios.delete(`${API}/notifications/clear-all`, { headers });
        fetchData();
        toast.success("All updates cleared");
      } catch (error) {
        toast.error("Couldn't clear updates");
      }
    };
    
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="text-center mb-6">
          <h2 className="font-serif text-2xl text-[#1A1A1A]">{t("Updates")}</h2>
          <p className="text-[#1A1A1A]/60 text-sm">{t("Stay updated on what's happening at Savi")}</p>
        </div>

        {combinedUpdates.length > 0 ? (
          <>
            {/* Clear All Button */}
            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllNotifications}
                className="text-xs text-[#1A1A1A]/50 hover:text-red-500"
              >
                <X className="w-3 h-3 mr-1" /> {t("Clear all")}
              </Button>
            </div>
            <div className="space-y-3">
              {combinedUpdates.map((item) => (
                <div 
                  key={item.id} 
                  onClick={() => {
                    if (!item.event_id || item.approval_result === "declined") return;
                    if (!item.read) {
                      if (item.type === 'announcement') markAnnouncementRead(item.id);
                      else markNotificationRead(item.id);
                    }
                    navigate(`/community?tab=activities&event=${item.event_id}`);
                  }}
                  className={`bg-white rounded-xl p-4 border transition-all ${
                    item.read ? "border-stone-100" : "border-[#C85A38]/30 shadow-sm"
                  } ${item.event_id && item.approval_result !== "declined" ? "cursor-pointer hover:border-[#C85A38]/40" : ""}`}
                  data-testid={`update-card-${item.id}`}
                >
                  <div className="flex items-start gap-3">
                    {/* Icon based on type */}
                    <div 
                      className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center cursor-pointer ${
                        item.type === 'announcement' ? (
                          item.icon === "dinner" ? "bg-[#C85A38]/10 text-[#C85A38]" :
                          item.icon === "activity" ? "bg-[#8C9A7C]/10 text-[#8C9A7C]" :
                          "bg-stone-100 text-stone-500"
                        ) : item.type === 'community' ? "bg-[#C85A38]/10 text-[#C85A38]" : "bg-stone-100 text-stone-500"
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!item.read) {
                          if (item.type === 'announcement') {
                            markAnnouncementRead(item.id);
                          } else {
                            markNotificationRead(item.id);
                          }
                        }
                      }}
                    >
                      {item.type === 'announcement' ? (
                        item.icon === "dinner" ? <Utensils className="w-5 h-5" /> :
                        item.icon === "activity" ? <Mountain className="w-5 h-5" /> :
                        <Bell className="w-5 h-5" />
                      ) : item.type === 'community' ? (
                        <Users className="w-5 h-5" />
                      ) : (
                        <Bell className="w-5 h-5" />
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      {item.title && (
                        <p className="font-medium text-[#1A1A1A] text-sm">{item.title}</p>
                      )}
                      <p className={`text-[#1A1A1A] break-words ${item.title ? 'text-xs mt-1 opacity-60' : 'text-sm'}`}>
                        {item.message}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <p className="text-xs text-[#1A1A1A]/40">
                          {new Date(item.created_at).toLocaleDateString(locale, {
                            day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                          })}
                        </p>
                        {item.type === 'community' && (
                          <span className="text-xs bg-[#C85A38]/10 text-[#C85A38] px-2 py-0.5 rounded-full">
                            {t("Community")}
                          </span>
                        )}
                      </div>
                      {item.event_id && item.pending_approval && user?.role === "admin" ? (
                        <div className="flex gap-2 mt-2.5" data-testid={`update-approval-${item.event_id}`}>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEventApproval(item.event_id, true); }}
                            className="px-3.5 py-1.5 rounded-full text-xs font-medium bg-[#8C9A7C] text-white hover:bg-[#7C8A6C] transition-colors"
                            data-testid="update-approve-btn"
                          >
                            ✓ Approve
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEventApproval(item.event_id, false); }}
                            className="px-3.5 py-1.5 rounded-full text-xs font-medium bg-white border border-stone-200 text-[#C85A38] hover:border-[#C85A38]/40 transition-colors"
                            data-testid="update-decline-btn"
                          >
                            ✕ Decline
                          </button>
                        </div>
                      ) : item.event_id && item.approval_result !== "declined" && !isRetreatGuest ? (
                        <div className="flex gap-2 mt-2.5" data-testid={`update-rsvp-${item.event_id}`}>
                          <button
                            onClick={(e) => { e.stopPropagation(); rsvpFromUpdate(item.event_id, true); }}
                            className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                              eventRsvpMap[item.event_id] === "in"
                                ? "bg-[#8C9A7C] text-white"
                                : "bg-[#C85A38] text-white hover:bg-[#B04A2C]"
                            }`}
                            data-testid="update-rsvp-in"
                          >
                            {eventRsvpMap[item.event_id] === "in" ? "You're in 🎉" : "I'm in 🙌"}
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); rsvpFromUpdate(item.event_id, false); }}
                            className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                              eventRsvpMap[item.event_id] === "out"
                                ? "bg-stone-200 border-stone-200 text-[#1A1A1A]"
                                : "bg-white border-stone-200 text-[#1A1A1A]/60 hover:border-stone-300"
                            }`}
                            data-testid="update-rsvp-out"
                          >
                            {eventRsvpMap[item.event_id] === "out" ? "Not going" : "Can't make it"}
                          </button>
                        </div>
                      ) : null}
                      {item.approval_result && (
                        <span
                          className={`inline-block mt-2 text-[11px] px-2 py-0.5 rounded-full ${
                            item.approval_result === "approved"
                              ? "bg-[#8C9A7C]/15 text-[#5C6A4C]"
                              : "bg-stone-100 text-stone-500"
                          }`}
                          data-testid="update-approval-result"
                        >
                          {item.approval_result === "approved" ? "✓ Approved" : "✕ Declined"}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {!item.read && (
                        <div className="w-2 h-2 rounded-full bg-[#C85A38]"></div>
                      )}
                      {/* Delete button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteUpdate(item);
                        }}
                        className="p-1.5 rounded-full hover:bg-red-50 text-[#1A1A1A]/30 hover:text-red-500 transition-colors"
                        title="Remove"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="bg-white rounded-2xl p-8 border border-stone-100 text-center">
            <Bell className="w-12 h-12 text-[#1A1A1A]/20 mx-auto mb-4" />
            <p className="text-[#1A1A1A]/60">{t("No updates yet")}</p>
            <p className="text-[#1A1A1A]/40 text-xs mt-1">{t("Check back soon for community updates")}</p>
          </div>
        )}
      </div>
    );
  };

  const renderProfile = () => (
    <div className="space-y-8 animate-fade-in">
      <div className="text-center">
        <div className="w-20 h-20 mx-auto rounded-full bg-[#F0EDE3] flex items-center justify-center mb-4 overflow-hidden">
          {user?.profile?.avatar || user?.avatar ? (
            <img 
              src={user?.profile?.avatar || user?.avatar} 
              alt={user?.name} 
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-3xl font-serif text-[#C85A38]">
              {user?.name?.charAt(0) || "?"}
            </span>
          )}
        </div>
        <h2 className="font-serif text-2xl text-[#1A1A1A]">{user?.name}</h2>
        <p className="text-[#1A1A1A]/60 text-sm">{user?.email}</p>
      </div>

      <div className="bg-white rounded-2xl p-5 border border-stone-100 space-y-4">
        <h3 className="font-medium text-[#1A1A1A]">Your Profile</h3>
        
        {user?.profile && (
          <div className="space-y-3 text-sm">
            {user.profile.nationality && (
              <div className="flex justify-between">
                <span className="text-[#1A1A1A]/60">Nationality</span>
                <span className="text-[#1A1A1A]">{user.profile.nationality}</span>
              </div>
            )}
            {user.profile.occupation && (
              <div className="flex justify-between">
                <span className="text-[#1A1A1A]/60">Occupation</span>
                <span className="text-[#1A1A1A]">{user.profile.occupation}</span>
              </div>
            )}
            {user.profile.current_project && (
              <div>
                <span className="text-[#1A1A1A]/60">Working on</span>
                <p className="text-[#1A1A1A] mt-1">{user.profile.current_project}</p>
              </div>
            )}
            {user.profile.bringing_pet && user.profile.pet_name && (
              <div className="flex justify-between items-center pt-2 border-t border-stone-100">
                <span className="text-[#1A1A1A]/60">Your Pet</span>
                <span className="text-[#1A1A1A]">
                  {user.profile.pet_type === "cat" ? "🐱" : "🐕"} {user.profile.pet_name}
                  {user.profile.pet_breed && <span className="text-[#1A1A1A]/60"> ({user.profile.pet_breed})</span>}
                </span>
              </div>
            )}
          </div>
        )}
        
        <Button 
          variant="outline" 
          className="w-full text-[#C85A38] border-[#C85A38] hover:bg-[#C85A38]/10"
          onClick={openEditProfile}
        >
          Edit Profile
        </Button>
      </div>

      {/* Install / save-to-phone instructions — always available in the Profile tab */}
      <InstallInstructions />

      {/* Edit Profile Modal */}
      {showEditProfile && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-xl text-[#1A1A1A]">Edit Profile</h2>
              <button 
                onClick={() => setShowEditProfile(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Profile Picture */}
              <div className="text-center">
                <div className="w-24 h-24 mx-auto rounded-full bg-[#F0EDE3] flex items-center justify-center overflow-hidden mb-3">
                  {editingProfile.avatar ? (
                    <img src={editingProfile.avatar} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-3xl font-serif text-[#C85A38]">
                      {editingProfile.name?.charAt(0) || "?"}
                    </span>
                  )}
                </div>
                <label className="inline-block">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    className="hidden"
                  />
                  <span className="text-sm text-[#C85A38] cursor-pointer hover:underline">
                    Change Photo
                  </span>
                </label>
                <ImageCropDialog
                  open={!!avatarCropFile}
                  file={avatarCropFile}
                  cropShape="round"
                  title="Position your photo"
                  onCancel={() => setAvatarCropFile(null)}
                  onCropped={uploadAvatar}
                />
              </div>

              {/* Name */}
              <div>
                <label className="text-sm text-[#1A1A1A]/60 block mb-1">Name</label>
                <input
                  type="text"
                  value={editingProfile.name}
                  onChange={(e) => setEditingProfile({ ...editingProfile, name: e.target.value })}
                  className="w-full px-4 py-2 border border-stone-200 rounded-xl"
                />
              </div>

              {/* Nationality */}
              <div>
                <label className="text-sm text-[#1A1A1A]/60 block mb-1">Nationality</label>
                <input
                  type="text"
                  value={editingProfile.nationality}
                  onChange={(e) => setEditingProfile({ ...editingProfile, nationality: e.target.value })}
                  className="w-full px-4 py-2 border border-stone-200 rounded-xl"
                  placeholder="e.g., British"
                />
              </div>

              {/* Occupation */}
              <div>
                <label className="text-sm text-[#1A1A1A]/60 block mb-1">Occupation</label>
                <input
                  type="text"
                  value={editingProfile.occupation}
                  onChange={(e) => setEditingProfile({ ...editingProfile, occupation: e.target.value })}
                  className="w-full px-4 py-2 border border-stone-200 rounded-xl"
                  placeholder="e.g., Software Developer"
                />
              </div>

              {/* Current Project */}
              <div>
                <label className="text-sm text-[#1A1A1A]/60 block mb-1">What are you working on?</label>
                <input
                  type="text"
                  value={editingProfile.current_project}
                  onChange={(e) => setEditingProfile({ ...editingProfile, current_project: e.target.value })}
                  className="w-full px-4 py-2 border border-stone-200 rounded-xl"
                  placeholder="e.g., Building a startup"
                />
              </div>

              {/* Fun Fact */}
              <div>
                <label className="text-sm text-[#1A1A1A]/60 block mb-1">Fun fact about you</label>
                <input
                  type="text"
                  value={editingProfile.fun_fact}
                  onChange={(e) => setEditingProfile({ ...editingProfile, fun_fact: e.target.value })}
                  className="w-full px-4 py-2 border border-stone-200 rounded-xl"
                  placeholder="Something interesting..."
                />
              </div>

              {/* Pet Section */}
              <div className="border-t border-stone-200 pt-4">
                <h3 className="font-medium text-[#1A1A1A] mb-3">🐾 Bringing a Furry Friend?</h3>
                
                <label className="flex items-center gap-2 mb-4 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingProfile.bringing_pet}
                    onChange={(e) => setEditingProfile({ 
                      ...editingProfile, 
                      bringing_pet: e.target.checked,
                      pet_type: e.target.checked ? "dog" : "",
                      pet_name: e.target.checked ? editingProfile.pet_name : "",
                      pet_breed: e.target.checked ? editingProfile.pet_breed : "",
                      pet_photo: e.target.checked ? editingProfile.pet_photo : ""
                    })}
                    className="w-4 h-4 rounded border-stone-300"
                  />
                  <span className="text-[#1A1A1A]">Yes, I'm bringing a pet!</span>
                </label>

                {editingProfile.bringing_pet && (
                  <div className="space-y-3 bg-[#F0EDE3] rounded-xl p-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm text-[#1A1A1A]/60 block mb-1">Pet Type</label>
                        <select
                          value={editingProfile.pet_type}
                          onChange={(e) => setEditingProfile({ ...editingProfile, pet_type: e.target.value })}
                          className="w-full px-3 py-2 border border-stone-200 rounded-xl bg-white"
                        >
                          <option value="dog">🐕 Dog</option>
                          <option value="cat">🐱 Cat</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-sm text-[#1A1A1A]/60 block mb-1">Pet's Name</label>
                        <input
                          type="text"
                          value={editingProfile.pet_name}
                          onChange={(e) => setEditingProfile({ ...editingProfile, pet_name: e.target.value })}
                          className="w-full px-3 py-2 border border-stone-200 rounded-xl"
                          placeholder="e.g., Luna"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-sm text-[#1A1A1A]/60 block mb-1">Breed (optional)</label>
                      <input
                        type="text"
                        value={editingProfile.pet_breed}
                        onChange={(e) => setEditingProfile({ ...editingProfile, pet_breed: e.target.value })}
                        className="w-full px-3 py-2 border border-stone-200 rounded-xl"
                        placeholder="e.g., Golden Retriever"
                      />
                    </div>

                    <div>
                      <label className="text-sm text-[#1A1A1A]/60 block mb-1">Pet Photo</label>
                      <div className="flex items-center gap-3">
                        <div className="w-16 h-16 rounded-xl bg-white border border-stone-200 flex items-center justify-center overflow-hidden">
                          {editingProfile.pet_photo ? (
                            <img src={editingProfile.pet_photo} alt="Pet" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-2xl">{editingProfile.pet_type === "cat" ? "🐱" : "🐕"}</span>
                          )}
                        </div>
                        <label className="flex-1">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handlePetPhotoUpload}
                            className="hidden"
                          />
                          <div className="px-4 py-2 bg-white rounded-xl border border-stone-200 text-center cursor-pointer hover:bg-stone-50">
                            <span className="text-sm text-[#1A1A1A]/60">Upload Photo</span>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Save Button */}
              <Button
                onClick={saveProfile}
                disabled={savingProfile}
                className="w-full bg-[#C85A38] hover:bg-[#B85540] text-white"
              >
                {savingProfile ? "Saving..." : "Save Profile"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Onboarding Status - Only for guests */}
      {user?.role !== "admin" && (
        <div className="bg-white rounded-2xl p-5 border border-stone-100 space-y-4 mt-8">
          <h3 className="font-medium text-[#1A1A1A]">Onboarding Status</h3>
          
          <div className="grid grid-cols-2 gap-3 text-sm">
            {[
              { label: "Profile", done: onboarding.profile_completed },
              { label: "House Rules", done: onboarding.rules_accepted },
              // Arrival / Legal & ID don't apply to volunteers — they don't
              // sign a paying-guest contract, so hide those rows entirely.
              ...(user?.is_volunteer ? [] : [
                { label: "Arrival", done: onboarding.arrival_submitted },
                { label: "Legal & ID", done: onboarding.legal_submitted },
                { label: "Reason for Stay", done: onboarding.stay_reason_submitted },
              ]),
              { label: "Your Rhythm", done: onboarding.rhythm_submitted },
              { label: "Lifestyle", done: onboarding.lifestyle_submitted },
              { label: "Activities", done: onboarding.activities_selected },
              { label: "Dietary", done: onboarding.dietary_submitted }
            ].map(({ label, done }) => (
              <div key={label} className="flex items-center gap-2">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                  done ? "bg-[#8C9A7C] text-white" : "bg-stone-100 text-stone-400"
                }`}>
                  {done ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                </div>
                <span className={done ? "text-[#1A1A1A]" : "text-[#1A1A1A]/40"}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Links Section */}
      <div className="space-y-4 mt-8">
        {/* Guest Handbook Link */}
        <Link to="/handbook">
          <div className="bg-gradient-to-r from-[#8C9A7C] to-[#6A9B84] rounded-2xl p-5 flex items-center justify-between hover:opacity-90 transition-opacity">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                <Home className="w-4 h-4 text-white" />
              </div>
              <div>
                <span className="font-medium text-white block">Guest Handbook</span>
                <span className="text-white/70 text-xs">House rules, tips & everything you need</span>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-white/60" />
          </div>
        </Link>

        {/* Arrival Info Link */}
        <Link to="/arrival">
          <div className="bg-gradient-to-r from-[#3D84A8] to-[#5B9EC4] rounded-2xl p-5 flex items-center justify-between hover:opacity-90 transition-opacity">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                <MapPin className="w-4 h-4 text-white" />
              </div>
              <div>
                <span className="font-medium text-white block">Arrival & Directions</span>
                <span className="text-white/70 text-xs">How to get to Villa Savi</span>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-white/60" />
          </div>
        </Link>

        {/* FAQ Link */}
        <Link to="/faq?from=app">
          <div className="bg-gradient-to-r from-[#9B8AC4] to-[#B8A9D9] rounded-2xl p-5 flex items-center justify-between hover:opacity-90 transition-opacity">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                <HelpCircle className="w-4 h-4 text-white" />
              </div>
              <div>
                <span className="font-medium text-white block">Frequently Asked Questions</span>
                <span className="text-white/70 text-xs">Common questions answered</span>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-white/60" />
          </div>
        </Link>

        {/* Leave a Review */}
        <a 
          href="https://g.page/r/Cdp75wG6jBNyEBM/review" 
          target="_blank" 
          rel="noopener noreferrer"
          data-testid="google-review-link"
        >
          <div className="bg-gradient-to-r from-[#C85A38] to-[#F4A261] rounded-2xl p-5 flex items-center justify-between hover:opacity-90 transition-opacity">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                <Star className="w-4 h-4 text-white fill-white" />
              </div>
              <div>
                <span className="font-medium text-white block">Leave Us a Review ⭐</span>
                <span className="text-white/70 text-xs">Share your Savi experience on Google</span>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-white/60" />
          </div>
        </a>

        {/* Change Password */}
        <button
          onClick={() => setShowChangePassword(true)}
          className="w-full text-left"
          data-testid="change-password-card"
        >
          <div className="bg-gradient-to-r from-[#6B705C] to-[#8A8F7C] rounded-2xl p-5 flex items-center justify-between hover:opacity-90 transition-opacity">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                <KeyRound className="w-4 h-4 text-white" />
              </div>
              <div>
                <span className="font-medium text-white block">Change Password</span>
                <span className="text-white/70 text-xs">Set your own password for this app</span>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-white/60" />
          </div>
        </button>
      </div>

      {/* Social Media Links */}
      <div className="bg-white rounded-2xl p-5 border border-stone-100 mt-8">
        <p className="font-medium text-[#1A1A1A] mb-3">Follow Savi</p>
        <div className="flex gap-3">
          <a 
            href={SAVI_SOCIAL.instagram}
            target="_blank" 
            rel="noopener noreferrer"
            className="flex-1 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 rounded-xl p-3 text-white text-center hover:opacity-90 transition-opacity"
          >
            <svg className="w-5 h-5 mx-auto mb-1" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
            <span className="text-xs font-medium">Instagram</span>
          </a>
          <a 
            href={SAVI_SOCIAL.facebook}
            target="_blank" 
            rel="noopener noreferrer"
            className="flex-1 bg-[#1877F2] rounded-xl p-3 text-white text-center hover:opacity-90 transition-opacity"
          >
            <svg className="w-5 h-5 mx-auto mb-1" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            <span className="text-xs font-medium">Facebook</span>
          </a>
          <a 
            href={SAVI_SOCIAL.whatsapp}
            target="_blank" 
            rel="noopener noreferrer"
            className="flex-1 bg-[#25D366] rounded-xl p-3 text-white text-center hover:opacity-90 transition-opacity"
          >
            <svg className="w-5 h-5 mx-auto mb-1" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            <span className="text-xs font-medium">WhatsApp</span>
          </a>
        </div>
      </div>

      <Button
        variant="outline"
        onClick={handleLogout}
        className="w-full rounded-xl border-red-200 text-red-500 hover:bg-red-50"
        data-testid="logout-btn"
      >
        <LogOut className="w-4 h-4 mr-2" />
        Sign Out
      </Button>
    </div>
  );

  const unreadNotifications = notifications.filter(n => !n.read).length;
  const unreadAnnouncements = announcements.filter(a => !a.read).length;
  const unreadCount = unreadNotifications + unreadAnnouncements;

  // Get current tab title
  const getTabTitle = () => {
    switch (activeTab) {
      case "home": return { title: t("Home"), subtitle: t("Welcome back") };
      case "notifications": return { title: t("Updates"), subtitle: t("Stay in the loop") };
      case "profile": return { title: t("Profile"), subtitle: t("Your Savi journey") };
      default: return { title: t("Home"), subtitle: t("Welcome back") };
    }
  };

  const currentTab = getTabTitle();

  return (
    <>
      {/* Welcome Animation Overlay */}
      {showWelcome && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#1A1A1A] animate-fade-in">
          <div className="text-center text-white">
            <img 
              src="https://customer-assets.emergentagent.com/job_184a0a73-7ed5-43f8-9a23-0cd69b83d3bb/artifacts/rcargp5m_no%20bkg.png" 
              alt="Savi" 
              className="h-24 w-auto mx-auto mb-6 animate-pulse"
            />
            <h1 className="font-serif text-4xl mb-2 animate-slide-up">Welcome to Savi!</h1>
            <p className="text-xl text-white/80 animate-slide-up stagger-1">
              {user?.name ? `Hello, ${user.name.split(' ')[0]}` : 'Your coliving journey begins'}
            </p>
            <p className="text-sm text-white/50 mt-4 animate-slide-up stagger-2">
              We're excited to have you 🌟
            </p>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-[#FBF7F1] pb-24 savi-bg-pattern">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#FBF7F1]/85 backdrop-blur-xl border-b border-stone-200/60">
        <div className="max-w-2xl mx-auto px-5 py-2.5 flex items-center justify-between">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2.5"
            data-testid="header-logo-btn"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_184a0a73-7ed5-43f8-9a23-0cd69b83d3bb/artifacts/rcargp5m_no%20bkg.png"
              alt="Savi"
              className="h-7 w-auto"
            />
            <span className="font-serif text-lg text-[#1A1A1A]">{currentTab.title}</span>
          </button>
          <div className="flex items-center gap-1">
            {user?.role === "admin" && (
              <Link
                to="/admin"
                className="text-xs font-semibold text-[#C85A38] px-2.5 py-1.5 rounded-full hover:bg-[#C85A38]/10"
                data-testid="header-admin-link"
              >
                Admin
              </Link>
            )}
            <button
              onClick={() => navigate("/dashboard?tab=notifications")}
              className="relative p-2 rounded-full text-[#1A1A1A]/60 hover:bg-stone-200/50 active:scale-[0.94] transition-transform"
              aria-label="Updates"
              data-testid="header-bell-btn"
            >
              <Bell className="w-5 h-5" strokeWidth={1.6} />
              {unreadCount > 0 && (
                <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 bg-[#C85A38] text-white text-[9px] font-bold rounded-full flex items-center justify-center px-1" data-testid="header-bell-badge">
                  {unreadCount}
                </span>
              )}
            </button>
            <button
              onClick={() => navigate("/dashboard?tab=profile")}
              className="ml-0.5 active:scale-[0.94] transition-transform"
              aria-label="Profile"
              data-testid="header-avatar-btn"
            >
              {user?.profile?.avatar || user?.avatar ? (
                <img
                  src={user?.profile?.avatar || user?.avatar}
                  alt={user?.name}
                  className="w-8 h-8 rounded-full object-cover border border-stone-200"
                />
              ) : (
                <span className="w-8 h-8 rounded-full bg-[#1A1A1A] text-white font-serif text-sm flex items-center justify-center">
                  {(user?.name || "?").charAt(0)}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-5 relative z-10">
        {activeTab === "home" && (
          <HomeTab
            user={user}
            weather={weather}
            todayActivities={todayActivities}
            todayEvents={todayEvents}
            weekEvents={weekEvents}
            todayVolunteerTasks={todayVolunteerTasks}
            steveMessage={steveMessage}
            announcements={announcements}
            youAsked={youAsked}
            countdown={countdown}
            todayRides={todayRides}
            weekRides={weekRides}
            progressPercent={progressPercent}
            showFirstWeekTips={showFirstWeekTips}
            onOpenNotifications={() => navigate("/dashboard?tab=notifications")}
            headers={headers}
            heroUrl={
              heroConfig.url
                ? (heroConfig.url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${heroConfig.url}` : heroConfig.url)
                : (heroConfig.loaded ? SAVI_IMAGES.villaAerial : null)
            }
          />
        )}
        {activeTab === "notifications" && renderNotifications()}
        {activeTab === "profile" && renderProfile()}
      </main>

      {/* Bottom Navigation */}
      <PortalBottomNav active={activeTab === "home" ? "home" : activeTab === "profile" ? "profile" : null} />

      {/* Confetti Celebration */}
      <Confetti trigger={showConfetti} />

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowChangePassword(false)}>
          <div className="bg-white rounded-3xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="change-password-modal">
            <h3 className="font-serif text-xl text-[#1A1A1A] mb-4">Change Password</h3>
            <div className="space-y-3">
              <div className="relative">
                <input
                  type={showPwFields ? "text" : "password"}
                  value={passwordForm.current}
                  onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
                  placeholder="Current password"
                  className="w-full h-11 px-4 pr-12 rounded-xl border border-stone-200 text-sm"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPwFields((s) => !s)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#1A1A1A]/40 hover:text-[#1A1A1A]/70"
                  aria-label={showPwFields ? "Hide passwords" : "Show passwords"}
                  data-testid="change-toggle-password-visibility"
                >
                  {showPwFields ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <input
                type={showPwFields ? "text" : "password"}
                value={passwordForm.next}
                onChange={(e) => setPasswordForm({ ...passwordForm, next: e.target.value })}
                placeholder="New password (min 6 characters)"
                className="w-full h-11 px-4 rounded-xl border border-stone-200 text-sm"
                data-testid="new-password-input"
              />
              <input
                type={showPwFields ? "text" : "password"}
                value={passwordForm.confirm}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                placeholder="Confirm new password"
                className="w-full h-11 px-4 rounded-xl border border-stone-200 text-sm"
                data-testid="confirm-password-input"
              />
              <Button
                onClick={changePassword}
                disabled={changingPassword}
                className="w-full bg-[#C85A38] hover:bg-[#B85540] text-white rounded-full"
                data-testid="submit-change-password-btn"
              >
                {changingPassword ? "Updating..." : "Update Password"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default GuestDashboard;
